import { cn } from "@/lib/utils";
import { Slot } from "@radix-ui/react-slot";
import { type ButtonHTMLAttributes, forwardRef } from "react";

/**
 * Small shadcn-style Button.
 *
 * Mirrors the minimal API used across the etf-website components: when
 * `asChild` is true, renders its single child through a Radix `Slot` so
 * callers (e.g. `<Button asChild><Link>…</Link></Button>`) preserve the
 * underlying element's semantics while inheriting Button styling.
 *
 * No variants are baked in here — each consumer composes the visual
 * treatment via `className` (Tailwind tokens). This keeps the file
 * footprint small while staying open to future CVA-driven variants.
 */
export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, asChild = false, type, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        // Only apply the default `type="button"` on actual <button> elements;
        // forwarding `type` to a Slot-rendered anchor would warn.
        type={asChild ? undefined : (type ?? "button")}
        className={cn(
          "inline-flex items-center justify-center font-[family-name:var(--font-instrument)] transition-colors duration-150 disabled:pointer-events-none disabled:opacity-60",
          className,
        )}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";
